/* Market watch.
 *
 * The panel a trader lives in. Every other panel on the screen is driven by
 * what is selected here, which makes an empty watchlist a dead terminal — the
 * chart, depth ladder and order ticket all fall back to their empty states and
 * the whole application looks broken. The API therefore provisions a starter
 * list for any user who has none, and this panel SELECTS the first row on load
 * so the terminal opens with something on it.
 *
 * The table is VIRTUALISED: only visible rows exist in the DOM. A watchlist of
 * 2,000 instruments updating several times a second would otherwise be
 * thousands of DOM nodes churning per frame.
 *
 * Each row carries what an Indian trader reads at a glance: symbol, the
 * exchange and segment it trades on, last traded price, and the change in both
 * absolute and percentage terms. A price the SIMULATOR produced is marked,
 * because a random-walk number and a market price look identical.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '@/store'
import { api } from '@/lib/api'
import { direction, fmtPrice } from '@/lib/format'
import { Panel } from '@/components/Panel'
import type { Quote } from '@/types'

const POLL_MS = 15_000  // the NSE candle feed updates once a minute

const ROW_H = 38
const OVERSCAN = 6

type SortKey = 'symbol' | 'change' | 'last'

export function Watchlist() {
  const quotes = useStore((s) => s.quotes)
  const applyQuotes = useStore((s) => s.applyQuotes)
  const selected = useStore((s) => s.selectedInstrument)
  const select = useStore((s) => s.selectInstrument)

  const [watchlists, setWatchlists] = useState<
    { id: string; name: string; is_default: boolean; item_count: number }[]>([])
  const [activeList, setActiveList] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load the user's lists, then poll the active one.
  useEffect(() => {
    let cancelled = false
    void api.watchlists()
      .then((w) => {
        if (cancelled) return
        setWatchlists(w.items)
        const def = w.items.find((x) => x.is_default) ?? w.items[0]
        setActiveList(def?.id ?? null)
        if (!def) setLoading(false)
      })
      .catch(() => {
        if (!cancelled) { setError('Watchlists could not be loaded.'); setLoading(false) }
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!activeList) return
    let cancelled = false
    let timer: number | undefined

    const poll = async () => {
      try {
        const rows = await api.watchlistQuotes(activeList)
        if (cancelled) return
        const batch = new Map<string, Quote>()
        for (const r of rows) {
          batch.set(r.symbol, {
            instrument: r.symbol,
            instrumentId: r.instrument_id,
            symbol: r.symbol,
            exchange: r.exchange,
            last: r.last ?? '',
            bid: r.bid ?? r.last ?? '',
            ask: r.ask ?? r.last ?? '',
            volume: r.volume ?? '0',
            open: r.open ?? undefined,
            high: r.high ?? undefined,
            low: r.low ?? undefined,
            prevClose: r.prev_close ?? undefined,
            change: r.change ?? undefined,
            changePct: r.change_pct != null ? String(r.change_pct) : undefined,
            // A row whose feed did not answer is STALE, not zero. The row stays
            // visible so the instrument can still be selected and charted.
            stale: r.last === null,
            ts: Date.now(),
          })
        }
        if (batch.size) applyQuotes(batch)
        setError(null)
      } catch {
        if (!cancelled) setError('Quotes are temporarily unavailable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
      if (!cancelled) timer = window.setTimeout(() => void poll(), POLL_MS)
    }
    void poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [activeList, applyQuotes])

  const [filter, setFilter] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('symbol')
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(400)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Track the real viewport height so the rendered slice adapts when the panel
  // is resized. A fixed guess either renders too few rows (visible gaps on a
  // tall panel) or too many (wasted work on a short one).
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight))
    ro.observe(el)
    setViewportH(el.clientHeight)
    return () => ro.disconnect()
  }, [])

  const rows = useMemo(() => {
    const list = [...quotes.values()]
    const f = filter.trim().toUpperCase()
    const filtered = f
      ? list.filter((q) => (q.symbol ?? String(q.instrument)).toUpperCase().includes(f))
      : list

    const name = (q: Quote) => q.symbol ?? String(q.instrument)
    return filtered.sort((a, b) => {
      if (sortKey === 'symbol') return name(a).localeCompare(name(b))
      if (sortKey === 'last') return (Number(b.last) || 0) - (Number(a.last) || 0)
      // Change: biggest movers first, and an unknown change sorts LAST rather
      // than sitting in the middle of the table pretending to be flat.
      const ac = a.changePct == null ? -Infinity : Number(a.changePct)
      const bc = b.changePct == null ? -Infinity : Number(b.changePct)
      return bc - ac
    })
  }, [quotes, filter, sortKey])

  // Open with something selected. A terminal whose chart, depth and ticket all
  // say "select an instrument" looks broken even when everything works.
  useEffect(() => {
    if (!selected && rows.length > 0) select(String(rows[0]!.instrument))
  }, [rows, selected, select])

  const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const visibleCount = Math.ceil(viewportH / ROW_H) + OVERSCAN * 2
  const slice = rows.slice(first, first + visibleCount)

  const activeName = watchlists.find((w) => w.id === activeList)?.name ?? 'Watchlist'

  return (
    <Panel
      title={`${activeName} · ${rows.length}`}
      noPadding
      actions={
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {watchlists.length > 1 && (
            <select
              value={activeList ?? ''}
              onChange={(e) => setActiveList(e.target.value)}
              aria-label="Watchlist"
              style={ctl}
            >
              {watchlists.map((w) => (
                <option key={w.id} value={w.id}>{w.name} ({w.item_count})</option>
              ))}
            </select>
          )}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label="Sort"
            style={ctl}
            title="Sort order"
          >
            <option value="symbol">A–Z</option>
            <option value="change">Movers</option>
            <option value="last">Price</option>
          </select>
        </div>
      }
      footer={
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter symbols…"
          aria-label="Filter watchlist"
          style={{ ...ctl, width: '100%', padding: '3px var(--sp-2)' }}
        />
      }
    >
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 92px',
        gap: 'var(--sp-2)', padding: '0 var(--sp-2)',
        height: 20, alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-panel)',
        position: 'sticky', top: 0, zIndex: 1,
        fontSize: 'var(--fs-2xs)', fontWeight: 700,
        letterSpacing: '0.05em', textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}>
        <span>Instrument</span>
        <span style={{ textAlign: 'right' }}>LTP · Chg</span>
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        style={{ height: '100%', overflowY: 'auto' }}
      >
        {loading && rows.length === 0 && (
          <Notice>Loading your watchlist…</Notice>
        )}
        {!loading && rows.length === 0 && (
          <Notice>
            {error ?? (filter
              ? `No symbol matches “${filter}”.`
              : 'This watchlist is empty. Add instruments to it to start trading.')}
          </Notice>
        )}

        <div style={{ height: rows.length * ROW_H, position: 'relative' }}>
          <div style={{ transform: `translateY(${first * ROW_H}px)` }}>
            {slice.map((q) => (
              <WatchRow
                key={String(q.instrument)}
                quote={q}
                selected={selected === String(q.instrument)}
                onSelect={() => select(String(q.instrument))}
              />
            ))}
          </div>
        </div>
      </div>
    </Panel>
  )
}

function WatchRow({ quote, selected, onSelect }: {
  quote: Quote
  selected: boolean
  onSelect: () => void
}) {
  const dir = direction(quote.changePct)
  const color =
    dir === 'up' ? 'var(--profit)' : dir === 'down' ? 'var(--loss)' : 'var(--flat)'
  const unknownChange = quote.changePct == null || quote.changePct === ''

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect() }}
      title={`${quote.symbol} · ${quote.exchange}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--sp-2)',
        padding: '0 var(--sp-2)',
        height: ROW_H, alignItems: 'center',
        cursor: 'pointer',
        background: selected ? 'var(--bg-active)' : undefined,
        borderLeft: selected ? '2px solid var(--accent)' : '2px solid transparent',
        borderBottom: '1px solid var(--border)',
        fontSize: 'var(--fs-sm)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: 600,
        }}>
          {/* A stale quote is marked explicitly. Showing a frozen price as if
              it were live is how a trader acts on information that no longer
              holds. */}
          {quote.stale && (
            <span title="No live quote for this instrument"
                  style={{ color: 'var(--warn)', fontSize: 9 }}>⚠</span>
          )}
          {quote.symbol ?? quote.instrument}
        </div>
        <div style={{
          fontSize: 9, color: 'var(--text-muted)',
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {quote.exchange ?? '—'}
        </div>
      </div>

      <div style={{ textAlign: 'right', minWidth: 88 }}>
        <div className="num" style={{
          fontWeight: 600,
          color: quote.stale ? 'var(--text-muted)' : 'var(--text-primary)',
        }}>
          {quote.stale ? '—' : fmtPrice(quote.last)}
        </div>
        <div className="num" style={{
          fontSize: 9,
          // An unknown change is a dash, never "0.00%". Zero means the market
          // did not move; unknown means nobody told us.
          color: unknownChange ? 'var(--text-muted)' : color,
        }}>
          {unknownChange ? '—' : (
            <>
              {Number(quote.change) >= 0 ? '+' : ''}
              {Number(quote.change).toFixed(2)}
              {' '}
              ({Number(quote.changePct) >= 0 ? '+' : ''}
              {Number(quote.changePct).toFixed(2)}%)
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: 'var(--sp-4) var(--sp-3)', textAlign: 'center',
      color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', lineHeight: 1.6,
    }}>
      {children}
    </div>
  )
}

const ctl: React.CSSProperties = {
  padding: '1px var(--sp-1)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
  fontSize: 'var(--fs-2xs)', fontFamily: 'inherit', maxWidth: 96,
}
